import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  AppWindow,
  User as UserIcon,
  Calendar,
  Lock,
  Globe,
  Users as UsersIcon,
  UserCheck,
} from 'lucide-react'
import {
  getApp,
  getAppActivityLog,
  listAllApps,
  type AdminApp,
  type App,
  type AppSlot,
  type ActivitySource,
  type AuditLogEvent,
} from '../../lib/api'
import { actionFilterOptions, slotBadgeClass } from '../../lib/activityActions'
import ActivityTimeline from '../../components/ActivityTimeline'

type SourceFilter = '' | ActivitySource
type SlotFilter = '' | AppSlot

export default function AdminAppDetail() {
  const { appName } = useParams<{ appName: string }>()
  const navigate = useNavigate()

  const [app, setApp] = useState<App | null>(null)
  // Owner / admin-only fields (email, user_id, access_level, etc.) live on
  // AdminApp, not the user-facing App returned by getApp(). Fetched in
  // parallel via listAllApps so the owner card renders without an extra
  // round-trip to a dedicated endpoint.
  const [adminApp, setAdminApp] = useState<AdminApp | null>(null)
  const [appError, setAppError] = useState<string | null>(null)
  const [events, setEvents] = useState<AuditLogEvent[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionFilter, setActionFilter] = useState<string>('')
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('')
  const [slotFilter, setSlotFilter] = useState<SlotFilter>('')
  const [page, setPage] = useState(0)
  const limit = 50

  useEffect(() => {
    if (!appName) return
    let cancelled = false
    Promise.all([
      getApp(appName).catch(() => null),
      listAllApps().catch(() => [] as AdminApp[]),
    ]).then(([appData, adminApps]) => {
      if (cancelled) return
      if (appData) setApp(appData)
      const match = adminApps.find((a) => a.name === appName) || null
      setAdminApp(match)
      if (!appData && !match) {
        setAppError('App not found')
      }
    })
    return () => {
      cancelled = true
    }
  }, [appName])

  const loadActivity = useCallback(async () => {
    if (!appName) return
    setLoading(true)
    try {
      const data = await getAppActivityLog(appName, {
        action: actionFilter || undefined,
        source: (sourceFilter || undefined) as ActivitySource | undefined,
        slot: (slotFilter || undefined) as AppSlot | undefined,
        limit,
        offset: page * limit,
      })
      setEvents(data.events)
      setTotal(data.total)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity')
    } finally {
      setLoading(false)
    }
  }, [appName, actionFilter, sourceFilter, slotFilter, page])

  useEffect(() => {
    loadActivity()
  }, [loadActivity])

  // Slot counts come straight off the event list. With limit=50, this only
  // reflects the current page — a fuller breakdown belongs to the
  // creator-analytics endpoints, not here.
  const slotCounts = events.reduce<Record<string, number>>((acc, ev) => {
    if (ev.action === 'app_visit' && ev.slot) {
      acc[ev.slot] = (acc[ev.slot] || 0) + 1
    }
    return acc
  }, {})

  const totalPages = Math.ceil(total / limit)
  const actionTypes = actionFilterOptions()

  if (appError) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">App not found</h2>
        <Link to="/admin/apps" className="text-primary hover:underline">
          Back to Apps
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin/apps')}
          className="p-2 rounded-lg hover:bg-secondary transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AppWindow className="w-5 h-5 text-muted-foreground" />
            {app?.display_name || appName}
          </h1>
          <p className="text-muted-foreground font-mono text-sm">/a/{appName}/</p>
        </div>
        {app?.url && (
          <a
            href={app.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-secondary transition-colors text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            Open
          </a>
        )}
      </div>

      {/* Owner / app metadata card — answers "whose app is this and how can
          this user reach them?" from the admin App Details view without
          bouncing back to the Apps or Users lists. */}
      {adminApp && (
        <div className="mb-6 rounded-xl border border-border bg-card p-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <div className="flex items-center gap-2 min-w-0">
              <UserIcon className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground">Owner</span>
              <Link
                to={`/admin/users/${adminApp.user_id}`}
                className="font-medium text-primary hover:underline truncate"
              >
                {adminApp.user_name || adminApp.user_email}
              </Link>
              {adminApp.user_name && (
                <span className="text-xs text-muted-foreground truncate">
                  ({adminApp.user_email})
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {adminApp.access_level === 'public' && <Globe className="w-4 h-4 text-muted-foreground" />}
              {adminApp.access_level === 'platform' && <UsersIcon className="w-4 h-4 text-muted-foreground" />}
              {adminApp.access_level === 'specific' && <UserCheck className="w-4 h-4 text-muted-foreground" />}
              {(adminApp.access_level === 'personal' || adminApp.access_level === 'link') && (
                <Lock className="w-4 h-4 text-muted-foreground" />
              )}
              <span className="text-muted-foreground">Access</span>
              <span className="font-medium">{adminApp.access_level}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Status</span>
              <span className="font-medium">{adminApp.status}</span>
            </div>
            {adminApp.created_at && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Created</span>
                <span className="font-medium">{new Date(adminApp.created_at).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Slot summary — clicking a card filters the history below to that
          slot, so admins can drill from the headline number to the events. */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {(['prod', 'dev', 'branch'] as AppSlot[]).map((slot) => {
          const active = slotFilter === slot
          return (
            <button
              key={slot}
              type="button"
              onClick={() => {
                setSlotFilter(active ? '' : slot)
                setPage(0)
              }}
              className={`text-left p-4 rounded-xl border bg-card transition-colors hover:bg-secondary/50 ${
                active ? 'border-primary ring-1 ring-primary/40' : 'border-border'
              }`}
              aria-pressed={active}
              title={active ? `Clear ${slot} filter` : `Filter history to ${slot} visits`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${slotBadgeClass(slot)}`}>
                  {slot}
                </span>
              </div>
              <p className="text-2xl font-bold">{slotCounts[slot] || 0}</p>
              <p className="text-xs text-muted-foreground">visits in current page</p>
            </button>
          )
        })}
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Interaction History</h2>
        <button
          onClick={loadActivity}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-secondary transition-colors disabled:opacity-50 text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value)
              setPage(0)
            }}
            className="px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {actionTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <select
          value={sourceFilter}
          onChange={(e) => {
            setSourceFilter(e.target.value as SourceFilter)
            setPage(0)
          }}
          className="px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">All Sources</option>
          <option value="audit_log">Audit Log</option>
          <option value="platform_event">Page Visits</option>
        </select>
        <select
          value={slotFilter}
          onChange={(e) => {
            setSlotFilter(e.target.value as SlotFilter)
            setPage(0)
          }}
          className="px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">All Slots</option>
          <option value="prod">Production</option>
          <option value="dev">Dev</option>
          <option value="branch">Branch</option>
        </select>
        <div className="text-sm text-muted-foreground">
          Showing {events.length} of {total} events
        </div>
      </div>

      <ActivityTimeline
        events={events}
        loading={loading}
        showUser
        onEventClick={(event) => {
          if (event.user_id) navigate(`/admin/users/${event.user_id}`)
        }}
      />

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}
